<template>
  <v-col align="center">
    <v-card width="1000">
      <v-card-title>
        <ProductCardHeader :product-id="product.id" title="Billing Summary" />
      </v-card-title>
      <v-card-text style="color: #4F4F4F">
        <v-row class="pl-5 title font-weight-regular" style="color: #004C97">
          Important:
        </v-row>
        <v-row class="pl-7">
          <div>
            You are purchasing a total of
            <span v-if="!isCancellation" class="font-weight-bold">{{ unitCount }}</span>
            <span v-else class="font-weight-bold red--text">{{ unitCount }}</span>
            {{ product.name }} licenses.
          </div>
        </v-row>
        <v-row class="font-weight-bold pl-5 pt-2">
          New {{ product.name }} Bill:
        </v-row>
        <v-simple-table class="pl-3">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left font-weight-bold subtitle-1 pl-0">Licensed Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in uniqueInsureSignUsers" :key="user.email">
                <td class="pl-0">{{ user.email }} <span v-if="user.isAdmin"> (Administrator)</span></td>
                <td>${{ product.productPricing.unitPrice }}</td>
              </tr>
              <tr>
                <td class="text-right font-weight-bold">
                  Monthly Subscription
                </td>
                <td class="font-weight-bold">
                  ${{ product.productPricing.unitPrice * uniqueInsureSignUsers.length }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div class="pl-3">
          <div class="text-left">Please agree to the terms below</div>
          <v-divider class="pl-3"></v-divider>
          <v-col class="pl-3">
            <div class="text-left mb-0"><a target="_blank" :href="hawkSoftLegalPageUrl">View the {{ product.name }} License Agreement</a></div>
            <v-checkbox v-model="agreedToHawkSoftTerms" hide-details class="mt-0" label="I agree to HawkSoft's terms and conditions, and agree to pay the amount shown above."></v-checkbox>
          </v-col>
          <v-divider class="pl-3"></v-divider>
          <v-col class="pl-3">
            <div class="text-left mb-0"><a target="_blank" :href="productLegalPageUrl">View the Formstack Sign License Agreement</a></div>
            <v-checkbox v-model="agreedToOtherTerms" hide-details class="mt-0" label="I agree to Formstack Sign's terms and conditions."></v-checkbox>
          </v-col>
          <v-divider class="pl-3"></v-divider>
        </div>

        <div v-if="!isCancellation" class="pl-3 pt-2 text-left">
          Once you click <em>Confirm Subscription</em>, your agency and users will have access to {{ product.name }}.
        </div>
        <div v-else class="pl-3 pt-2 red--text text-left">
          I want to disable {{ product.name }} immediately for all users.<br />
          Documents in process that have not been attached will be lost.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-row align-content="center">
          <v-col cols="2" class="text-left">
            <v-btn block @click="back()">Back</v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="6" class="text-center">
            <span v-if="errorMessage" class="red--text text-center">{{ errorMessage }}</span>
          </v-col>
          <v-col cols="3" class="text-center">
            <v-btn v-if="isCancellation && !savingUsers" color="#FF9E15" :disabled="!agreedToTerms" class="caption" @click="cancellationDialog = true">
              Disable {{ product.name }}
            </v-btn>
            <v-btn v-if="!isCancellation && !savingUsers" color="#FF9E15" :disabled="!agreedToTerms" @click="confirm()">
              Confirm Subscription
            </v-btn>
            <v-progress-circular v-if="savingUsers" indeterminate color="#004C97" />
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="cancellationDialog" width="600">
      <v-card>
        <v-card-title>
          Confirm User Removal
        </v-card-title>
        <v-card-text>
          <v-form ref="cancellationForm" v-model="valid">
            <div>
              What is the primary reason that you want to cancel {{ product.name }}? (select one)
            </div>
            <div class="pa-2">
              <v-radio-group v-model="cancellationReason">
                <v-radio v-for="reason in cancellationReasons"
                         :key="reason.code"
                         :label="reason.description"
                         :value="reason">

                </v-radio>
              </v-radio-group>
            </div>
            <v-textarea v-model="cancellationComments"
                        class="form-control"
                        form-control-name="comments"
                        label="Additional Comments:"
                        :rules="commentsRules"
            >
            </v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn @click="cancellationDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!valid" @click="cancelSubscription">Disable {{ product.name }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ProductCardHeader from '@/components/products/shared/ProductCardHeader';
import productIdMappingMixin from '@/mixins/productIdMappingMixin';

export default {
  name: 'BillingConfirmation',
  components: {
    ProductCardHeader
  },
  mixins: [productIdMappingMixin],
  data () {
    return {
      hawkSoftLegalPageUrl: 'https://www.hawksoft.com/terms/hawksoft-esignature/',
      productLegalPageUrl: 'https://www.formstack.com/legal/master-services-agreement',
      agreedToHawkSoftTerms: false,
      agreedToOtherTerms: false,
      openCancellationModal: false,
      savingUsers: false,
      errorMessage: '',
      cancellationDialog: false,
      valid: true,
      cancellationReasons: [
        {
          code: 'Lost to Competitor',
          description: 'We chose a different E-Signature Vendor.'
        },
        {
          code: 'Lack of Interest',
          description: "We don't need any E-Signature Service."
        },
        {
          code: 'Performance',
          description: "It didn't work the way we needed it to."
        },
        {
          code: 'Financial',
          description: 'The price was too high.'
        },
        {
          code: 'Security Concerns',
          description: "It didn't meet our security needs."
        },
        {
          code: 'Other',
          description: 'Other'
        }
      ],
      cancellationReason: {},
      cancellationComments: ''
    };
  },
  computed: {
    ...mapState(['selectedInsureSignUsers', 'uniqueInsureSignUsers']),
    ...mapGetters(['getProductByProductId']),
    product () {
      return this.getProductByProductId(this.getProductId('eSignature'));
    },
    unitCount () {
      return this.uniqueInsureSignUsers.length;
    },
    isCancellation () {
      return this.uniqueInsureSignUsers.length === 0;
    },
    agreedToTerms () {
      return this.agreedToHawkSoftTerms && this.agreedToOtherTerms;
    },
    commentsRules () {
      const rules = [];
      if (this.cancellationReason.code === 'Other') {
        const rule = v => v.length > 0 || 'Please leave an additional comment.';
        rules.push(rule);
      }
      return rules;
    }
  },
  created: function () {
    this.cancellationReason = this.cancellationReasons[0];
  },
  methods: {
    ...mapActions(['setProductSubscription']),
    back () {
      this.$router.push('/product/eSignature/userSelection');
    },
    getButtonMessage () {
      return this.isCancellation()
        ? 'Disable ' + this.product.name
        : 'Confirm Subscription';
    },
    async confirm () {
      this.savingUsers = true;

      var saveUsersResp = await this.$api.saveInsureSignUsers(this.selectedInsureSignUsers);
      if (saveUsersResp.code !== 0) {
        this.savingUsers = false;
        this.errorMessage = 'Error saving users, please try again';
        return;
      }

      if (!this.product.isSubscribed) {
        var res = await this.$api.setSubscriptionDetailed(this.product.id, true);
        if (res.code !== 0) {
          this.savingUsers = false;
          this.errorMessage = 'Error subscribing to product, please contact customer support';
          return;
        }

        await this.$api.sendAgencyPurchasedEmail();
      }

      this.setProductSubscription({ productId: this.product.id, subscription: !this.isCancellation });
      this.savingUsers = false;
      this.$router.push('/product/eSignature/nextSteps');
    },
    async cancelSubscription () {
      var feedback = `${this.cancellationReason.code}: ${this.cancellationReason.description}.`;
      if (this.cancellationComments !== null && this.cancellationComments.trim() !== '') {
        feedback += ` Feedback: ${this.cancellationComments}`;
      }

      var response = await this.$api.setSubscriptionDetailed(this.product.id, false, feedback, true);
      if (response.code !== 0) {
        this.errorMessage = 'Error canceling subscription, please try again or contact customer support.';
        return;
      } else {
        this.setProductSubscription({ productId: this.product.id, subscription: !this.isCancellation });
      }

      this.$router.push('/product/eSignature/nextSteps');
    }
  }
};

</script>
